import { useCallback, useMemo, useRef, useState } from 'react';
import {
  AiDetectionReport,
  AiDetectionMetricRow,
  buildAiDetectionReport,
} from '../../../../domain/aiDetectionMetrics';
import { fetchAiDetectionMetrics } from '../../../../services/aiDetection';
import { logGlobalError } from '../../../../utils/globalErrorLogger';
import { sortMetricRows, SortKey, TableSort } from './sort';

export function useAiDetectionTableViewModel(rows: AiDetectionMetricRow[]) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<TableSort>({ key: 'warningRank', direction: 'ascending' });
  const sortBy = useCallback((key: SortKey) => {
    setSort((current) => ({
      key,
      direction: current.key === key
        ? current.direction === 'ascending' ? 'descending' : 'ascending'
        : key === 'name' || key === 'warningRank' ? 'ascending' : 'descending',
    }));
  }, []);
  const visibleRows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const filteredRows = normalizedQuery ? rows.filter((row) => (
      row.name.toLowerCase().includes(normalizedQuery)
      || (row.email ?? '').toLowerCase().includes(normalizedQuery)
      || row.expertID.toLowerCase().includes(normalizedQuery)
    )) : rows;
    return sortMetricRows(filteredRows, sort);
  }, [query, rows, sort]);

  return { query, setQuery, sort, sortBy, visibleRows };
}

export interface AiDetectionDashboardViewModel {
  busy: boolean;
  loading: boolean;
  error: string | null;
  report: AiDetectionReport;
  initialize: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useAiDetectionDashboardViewModel(): AiDetectionDashboardViewModel {
  const [busy, setBusy] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<AiDetectionReport>(() => buildAiDetectionReport([]));
  const fetchSeqRef = useRef<number>(0);

  const load = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const metrics = await fetchAiDetectionMetrics();
      if (seq === fetchSeqRef.current) setReport(buildAiDetectionReport(metrics));
    } catch (e: unknown) {
      if (seq !== fetchSeqRef.current) return;
      const message = e instanceof Error ? e.message : 'Failed to load AI detection metrics';
      setError(message);
      logGlobalError(e, { context: 'AiDetectionDashboardTool.load' });
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, []);

  const initialize = useCallback(async () => {
    setBusy(true);
    try {
      await load();
    } finally {
      setBusy(false);
    }
  }, [load]);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  return { busy, loading, error, report, initialize, refresh };
}
